<script setup lang="ts">
  import {useAsyncData} from '#app';
  import {useRoute} from 'vue-router';
  import {DateTime} from 'luxon';
  import {ref} from 'vue';
  import PluginLayout from '~/components/PluginLayout.vue';
  import {
    computed,
    definePageMeta,
    useAppStore,
    useFetch,
    usePluginStoreStore,
    watch,
  } from '#imports';
  import {useEnv} from '~/helpers/env';
  import type {PaginatedResponse, PluginReview} from '~';
  import CDropdown from '~/components/ui/CDropdown.vue';
  import ChevronLeftIcon from '~/components/ui/icons/outline/ChevronLeftIcon.vue';
  import ChevronRightIcon from '~/components/ui/icons/outline/ChevronRightIcon.vue';
  import ProfilePhoto from '~/components/ProfilePhoto.vue';

  definePageMeta({
    layout: 'site',
  });

  const route = useRoute();
  const appStore = useAppStore();
  const {craftConsoleUrl, apiEndpointUrl, getPluginReviewUrl} = useEnv();
  const pluginStoreStore = usePluginStoreStore();

  const params = ref({
    orderBy: 'dateCreated',
    direction: 'desc',
    page: 1,
    ...route.query,
  });

  watch(params, (newValue) => {
    const queryString = new URLSearchParams(newValue as any).toString();
    window.history.replaceState({}, '', `?${queryString}`);
  }, { deep: true });

  const plugin = computed(() => pluginStoreStore.plugin);
  const {data: response, status, refresh} = useFetch<PaginatedResponse<PluginReview>>(
    `plugin/${route.params.handle}/reviews`,
    {
      baseURL: apiEndpointUrl,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      query: params,
      method: 'GET',
      watch: false,
    },
  );

  watch(params, () => {
    refresh();
  }, { deep: true });

  await useAsyncData(async () => {
    if (
      !pluginStoreStore.plugin ||
      pluginStoreStore.plugin.handle !== route.params.handle
    ) {
      await pluginStoreStore.getPluginDetailsByHandle(
        route.params.handle as string,
      );

      if (plugin.value) {
        appStore.updatePageMeta({
          title: plugin.value?.name,
          description: plugin.value?.shortDescription,
        });
      }
    }
  });

  const stats = computed(() => {
    return plugin.value?.reviewStats;
  });

  const reviewUrl = computed(() => {
    return plugin.value
      ? `${craftConsoleUrl}/accounts/me/plugin-store/reviews/${plugin.value?.handle}`
      : null;
  });

  const orderByOptions = [
    {label: 'Date Created', value: 'dateCreated'},
    {label: 'Rating', value: 'rating'},
  ];

  const directionOptions = [
    {label: 'Ascending', value: 'asc'},
    {label: 'Descending', value: 'desc'},
  ];

  function nextPage() {
    if (response.value) {
      goToPage(response.value?.current_page + 1);
    }
  }

  function previousPage() {
    if (response.value) {
      goToPage(response.value?.current_page - 1);
    }
  }

  function goToPage(page: number) {
    if (!response.value || !page) {
      return;
    }

    params.value.page = page;
  }
</script>

<template>
  <plugin-layout>
    <template v-if="response">
      <template v-if="stats && stats.totalReviews > 0">
        <PluginRatingStats
          :stats="stats"
          :handle="plugin?.handle"
          class="mb-10"
        />
      </template>

      <div>
        <div class="grid sm:grid-cols-2 items-center mb-8 gap-2">
          <div>
            <UiCBtn :href="getPluginReviewUrl(plugin?.handle)">
              Leave a review
            </UiCBtn>
          </div>
          <div class="sm:justify-self-end">
            <div class="flex flex-wrap gap-2 items-center">
              <UiCSpinner
                v-if="status === 'pending'"
                class="hidden sm:block mr-2"
              />

              <div>
                <label class="sr-only" for="order-by-select">Order by</label>
                <c-dropdown
                  id="order-by-select"
                  v-model="params.orderBy"
                  :options="orderByOptions"
                />
              </div>

              <div>
                <label class="sr-only" for="direction-select">Direction</label>
                <c-dropdown
                  id="direction-select"
                  v-model="params.direction"
                  :options="directionOptions"
                />
              </div>

              <UiCSpinner v-if="status === 'pending'" class="sm:hidden mr-2" />
            </div>
          </div>
        </div>
        <div
          v-if="response.data && response.data.length > 0"
          class="grid md:grid-cols-2 gap-x-10 gap-y-12"
        >
          <template v-for="review in response.data" :key="review.id">
            <div class="">
              <RatingStars :rating="review.rating" />

              <div class="mt-2">
                {{ review.comment }}
              </div>

              <div class="mt-4 flex gap-4">
                <ProfilePhoto :url="review.author.photo" />

                <div>
                  <div>
                    <strong>{{ review.author.name }}</strong>
                  </div>

                  <div
                    class="flex text-xs text-gray-500 dark:text-gray-400 gap-1"
                  >
                    <span>
                      {{ DateTime.fromISO(review.dateCreated).toRelative() }}
                    </span>
                    <template v-if="review.dateUpdated !== review.dateCreated">
                      <span>•</span>
                      <span>
                        Edited
                        {{ DateTime.fromISO(review.dateUpdated).toRelative() }}
                      </span>
                    </template>
                  </div>
                </div>
              </div>

              <template v-if="review.comments.length > 0">
                <div
                  class="mt-6 border-l-4 border-l-blue-200 dark:border-l-blue-800 pl-4"
                >
                  <strong class="block mb-1">Developer Response</strong>
                  <template
                    v-for="comment in review.comments"
                    :key="comment.id"
                  >
                    {{ comment.comment }}
                    <div
                      class="flex text-xs text-gray-500 dark:text-gray-400 gap-1 mt-2"
                    >
                      <span>
                        {{ DateTime.fromISO(comment.dateCreated).toRelative() }}
                      </span>
                      <template
                        v-if="comment.dateUpdated !== comment.dateCreated"
                      >
                        <span>•</span>
                        <span>
                          Edited
                          {{
                            DateTime.fromISO(comment.dateUpdated).toRelative()
                          }}
                        </span>
                      </template>
                    </div>
                  </template>
                </div>
              </template>
            </div>
          </template>
        </div>
        <div v-else>
          <div class="p-12 md:py-24 border dark:border-gray-600 rounded-md">
            <div class="text-center">
              <p>This plugin doesn't have any reviews with comments.</p>
              <div class="mt-4">
                <UiCBtn v-if="reviewUrl" :href="reviewUrl" target="_blank">
                  Leave a review
                </UiCBtn>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="response && response.last_page > 1"
          class="mt-12 flex justify-between border-t pt-4"
        >
          <UiCBtn small :disabled="params.page === 1" @click="previousPage">
            <ChevronLeftIcon class="w-4 h-4" />
          </UiCBtn>
          <div class="flex gap-2 items-center justify-center">
            <template v-for="i in response.last_page" :key="i">
              <template v-if="i === response.current_page">
                <span
                  class="font-medium px-3 py-2 rounded-md border leading-4 text-sm border-blue-400 bg-blue-50 dark:bg-blue-600/40 text-blue-600 dark:text-blue-100"
                >
                  {{ i }}
                </span>
              </template>
              <template v-else>
                <UiCBtn small @click="goToPage(i)">
                  {{ i }}
                </UiCBtn>
              </template>
            </template>
          </div>
          <UiCBtn
            small
            :disabled="params.page === response.last_page"
            @click="nextPage"
          >
            <ChevronRightIcon class="w-4 h-4" />
          </UiCBtn>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="p-12 md:py-24 border rounded-md">
        <div class="text-center">
          <p>This plugin doesn't have any reviews yet.</p>
          <div class="mt-4">
            <UiCBtn v-if="reviewUrl" :href="reviewUrl" target="_blank">
              Leave a review
            </UiCBtn>
          </div>
        </div>
      </div>
    </template>
  </plugin-layout>
</template>

<style scoped lang="scss"></style>
